import { getConfirmLabel, useTokenValues } from '@jds/react-engine';
import {
  getDisabledYears,
  getNormalisedDate,
  loadedMonths
} from '../jdsdatepickerdesktop/dateLogic';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { DatePickerProps } from '../jdsdatepickerdesktop/logic';
import { useToggle } from '../../hooks/useToggle';

const YEAR_COUNT = 50;

export const withLogic =
  (
    Component: React.ComponentType<DatePickerProps>
  ): React.FC<DatePickerProps> =>
  (props: DatePickerProps) => {
    const {
      onClose,
      monthSelectable = true,
      yearSelectable = true,
      close: userClose = true,
      open,
      ...rest
    } = props;
    const closeProp = typeof open === 'undefined' ? userClose : !open;
    const [state, setState] = useState('default');

    const { day, month, year } = getNormalisedDate(props.value);
    const [selectedMonth, setSelectedMonth] = useState(month);
    const [selectedDate, setSelectedDate] = useState({
      date: day,
      month,
      year
    });
    const [selectedYear, setSelectedYear] = useState(year);
    const { selected, toggle, close } = useToggle(props.open, !closeProp);

    const scrollCloseListener = useCallback(() => {
      close();
      onClose?.();
    }, [close, onClose]);

    useEffect(() => {
      window.addEventListener('scroll', scrollCloseListener);
      return () => {
        window.removeEventListener('scroll', scrollCloseListener);
      };
    }, [scrollCloseListener]);

    useEffect(() => {
      const { day, month, year } = getNormalisedDate(props.value);
      setSelectedDate({ date: day, month, year });
      setSelectedYear(year);
      setSelectedMonth(month);
    }, [props.value]);

    const months = loadedMonths(
      selectedMonth - 1,
      selectedYear,
      monthSelectable,
      props.min,
      props.max
    );
    const tokens = useTokenValues();
    const language = tokens.lang;
    const confirmLabelLocal = useMemo(() => {
      return getConfirmLabel(language);
    }, [language]);
    if (!selected) return props.children as JSX.Element;

    return (
      <Component
        {...{
          ...rest,
          confirmDate: new Date(
            selectedDate.year,
            selectedDate.month - 1,
            selectedDate.date
          ).toLocaleDateString('en-GB', {
            weekday: 'short',
            day: '2-digit',
            month: 'long',
            year: 'numeric'
          }),
          _confirmLabel: confirmLabelLocal,
          calendarData: {
            disabledDates: props.disabledDates,
            dateFilter: props.dateFilter,
            min: props.min,
            max: props.max
          },
          open: selected,
          value: new Date(
            selectedDate.year,
            selectedDate.month - 1,
            selectedDate.date
          ),
          onClose: () => {
            if (selected) {
              toggle();
            }
            onClose?.();
          },
          onClick: (ev: React.MouseEvent<HTMLDivElement>) =>
            ev.stopPropagation(),
          date: selectedDate.date,
          month: selectedMonth,
          year: selectedYear,
          monthName: [selectedMonth - 1]?.slice(0, 3),
          months,
          state,
          offsetX: 0,
          offsetY: 0,
          yearData: {
            count: YEAR_COUNT,
            min: selectedYear - YEAR_COUNT / 2,
            max: selectedYear + YEAR_COUNT / 2,
            disabled: getDisabledYears(
              selectedYear - YEAR_COUNT / 2,
              selectedYear + YEAR_COUNT / 2,
              props.min,
              props.max
            )
          },
          _onYearClick: () => setState('year_view'),
          _onMonthClick: () => setState('month_view'),
          _onBack: () => setState('default'),
          onSelect: (num: number, disabled?: boolean) => {
            if (!disabled) {
              setSelectedDate({
                date: num,
                month: selectedMonth,
                year: selectedYear
              });
            }
            setState('default');
          },
          _onConfirm: () => {
            props.onSelect?.(
              new Date(selectedDate.year, selectedDate.month, selectedDate.date)
            );
            props.onChange?.(
              new Date(selectedDate.year, selectedDate.month, selectedDate.date)
            );

            props.onConfirm?.(
              new Date(selectedDate.year, selectedDate.month, selectedDate.date)
            );
            close();
            onClose?.();
          },
          _onMonthSelect: (i: number) => {
            if (monthSelectable) {
              setSelectedMonth(i);
              setState('default');
              props.onMonthSelect?.();
            }
          },
          _onYearSelect: (i: number) => {
            if (yearSelectable) {
              setSelectedYear(i);
              setState('default');
              props.onYearSelect?.();
            }
          },
          _onPrev: () =>
            setSelectedMonth((m) => {
              if (m === 1) {
                setSelectedYear(selectedYear - 1);
                return 12;
              }
              return m - 1;
            }),
          _onNext: () =>
            setSelectedMonth((m) => {
              if (m === 12) {
                setSelectedYear(selectedYear + 1);
                return 1;
              }
              return m + 1;
            })
        }}
      >
        {props.children}
      </Component>
    );
  };

export { DatePickerProps };
